import {
  Card as MuiCard,
  Box,
  Typography as Typo,
  Paper,
  useTheme,
  styled,
  TypographyProps,
  TooltipProps,
  Tooltip,
  tooltipClasses,
  Button,
  useMediaQuery,
} from '@mui/material';
import {useState, useEffect, useMemo} from 'react';
import {Image, ImageProps} from '@jobseeker/lotus/components/Image';
import {getRandomColor} from '@jobseeker/lotus/theme';
import {useRouter} from 'next/router';
import {useIsLargeDevice} from '@jobseeker/lotus/hooks';
import {DefaultJobLogo, Star} from '@jobseeker/lotus/icons';
import {addUtmParamsToUrl} from '@jobseeker/lotus/utils';
import {getKeyFromPath, openUrl} from '@jobseeker/common-core/utils/common';
import {
  getRefTagAndOrigin,
  TrackEventUBA,
  getUbaPageName,
} from '@jobseeker/common-core/utils';
import {fontWeight, textAlign} from '@mui/system';
import Link from 'next/link';

const Card = styled(MuiCard)(({theme}) => ({
  borderRadius: theme.spacing(2),
}));
interface ImageWithFallbackProps extends ImageProps {
  fallback: any;
  itemId: any;
}

export const Typography = styled(Typo)<any>((props: TypographyProps) => ({
  display: 'flex',
  alignItems: 'center',
}));
const TooltipHirist = styled(({className, ...props}: TooltipProps) => (
  <Tooltip {...props} classes={{popper: className}} />
))(({theme}) => ({
  ul: {
    margin: 0,
    padding: '14px 15px',
  },
  [`& .${tooltipClasses.tooltip}`]: {
    padding: theme.spacing(1, 2),
    borderRadius: theme.spacing(1),
    backgroundColor: '#1c2d41',
    color: '#fff',
    fontSize: 16,
    // border: '1px solid #dadde9',
  },
  '.MuiTooltip-arrow:before': {
    backgroundColor: '#1c2d41',
  },
}));
const Dot2 = () => (
  <Box
    component="span"
    sx={{
      width: 4,
      height: 4,
      bgcolor: 'black',
      opacity: 0.5,
      borderRadius: '50%',
      display: 'inline-block',
      mx: '1.75px',
      mr: 1,
      verticalAlign: 'middle',
    }}
  />
);

export const JoblistPaper = styled(Paper)<any>((props: any) => ({
  borderRadius: 0,
  position: 'relative',
  display: 'flex',
  gap: props.theme.spacing(1.5),
  // cursor: 'pointer',
  '.joblist__logo': {
    borderRadius: props.theme.spacing(1.5),
    border: '0.6px solid #7E7E7E33',
  },
  '.joblist__right_element': {
    position: 'absolute',
    width: 'fit-content',
    right: props.theme.spacing(2.5),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-end',
    textAlign: 'right',
    // padding: props.theme.spacing(2),
    top: 0,
    bottom: 0,
  },
  '.joblist__subtitle': {
    p: {
      wordBreak: 'break-all',
      color: props.theme.palette.text.secondary,
    },
  },
  '.save-icon': {
    display: 'none',
  },
  ':hover': {
    '.save-icon': {
      display: 'unset',
    },
  },
}));

const CompanyLogo = ({
  companyId,
  alt,
  randomColor,
  fallbackCompanyIds,
}: any) => {
  const theme = useTheme();
  const [imgError, setImgError] = useState(false);

  const handleImageError = () => {
    setImgError(true);
  };

  return (
    <>
      {!imgError ? (
        <Image
          src={`https://staticlogo.hirist.com/webp/${
            companyId ? companyId : fallbackCompanyIds
          }-100X100.webp`}
          alt={alt}
          width={50}
          height={50}
          style={{
            backgroundColor: '#fff',
            border: '1px solid #ececec',
            borderRadius: 10,
          }}
          onError={handleImageError}
          unoptimized
        />
      ) : (
        <Box
          className="joblist__logo"
          sx={{
            background: randomColor,
            p: theme.spacing(1.5),
            height: 'min-content',
            lineHeight: 0,
          }}
        >
          <Image
            src={DefaultJobLogo}
            alt={'Logo'}
            width={25}
            height={25}
            objectFit="cover"
            quality={50}
          />
        </Box>
      )}
    </>
  );
};

const JobListComponent = (e: any) => {
  const data = e.data;
  const theme = useTheme();
  const randomColor = useMemo(
    () => getRandomColor(data?.centralisedCompanyId),
    [data?.centralisedCompanyId]
  );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [open, setOpen] = useState(false);
  const IsLarge = useIsLargeDevice();

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    event?.preventDefault();
    event?.stopPropagation();
    setAnchorEl(event.currentTarget);
    setOpen(true);
  };
  const {companyId, companyName, description, location, jobCount} =
    data.showcaseData ? data.showcaseData : data;
  const router = useRouter();

  useEffect(() => {
    const handler = () => {
      setOpen(false);
    };
    window?.addEventListener('scroll', handler);
    return () => {
      window.removeEventListener('scroll', handler);
    };
  }, []);

  const handleClose = () => {
    setAnchorEl(null);
    setOpen(false);
  };
  console.log({data});

  const MainComponent = Card;

  const isLarge = useIsLargeDevice();

  const BenTOSHow = isLarge ? 4 : 2;

  const introText = description
    ? description
        ?.replace(/<b>|<\/b>|<br\/>|<p>|<\/p>/g, '')
        .substring(0, 170) + '...'
    : `Explore opportunities at ${data?.ambitionBoxName}. Please click on View All Jobs to see the available jobs....`;
  const trackEvent = () => {
    TrackEventUBA('reviewsClick', 'click', {
      ref: getKeyFromPath('ref') || '',
      sessionUsername: sessionStorage.getItem('userId') || '',
      page: 'CompanyDetails',
      companyID: companyId ?? '',
      companyName: companyName ?? '',
    });
  };

  const createMarkup = (html: string) => ({__html: html});

  return (
    <MainComponent
      data-testid="company-tile-container"
      className="joblist-card-v2"
      color="gray.dark"
      onClick={() => {}}
      sx={{
        mb: '20px',

        position: 'relative',
        overflow: 'unset',
        transition: 'all ease 0.2s',
        width: {xxs: '85%', md: '92%'},
        ml: {xxs: 3.5, md: 4},
        ':hover': {
          boxShadow: 'rgba(0, 0, 0, 0.09) 0px 5px 15px 0px;',
        },
      }}
    >
      <JoblistPaper
        data-testid="company-tile-content"
        elevation={0}
        sx={{
          py: {xxs: 1.25},
          borderRadius: 2,

          pl: 5,
          pr: 0,
        }}
      >
        <Box
          data-testid="company-tile-logo-container"
          sx={{
            display: 'flex',
            position: 'absolute',
            left: '-25px',
            top: theme.spacing(1.25),
          }}
        >
          <CompanyLogo
            companyId={companyId}
            alt={'alt'}
            randomColor={randomColor}
            fallbackCompanyIds={data?.companyIds}
          />
        </Box>
        <Box data-testid="company-tile-details" display={'flex'} width={'100%'}>
          <Box
            sx={{
              // display: 'flex',
              // flexDirection: 'column',
              // justifyContent: 'space-between',
              width: {xxs: '95%', md: '75%'},
            }}
          >
            <Box
              data-testid="company-tile-header"
              display={{xxs: 'inline-block', md: 'flex'}}
              width={'90%'}
            >
              <Typography
                data-testid="company-tile-title"
                variant="subtitle2"
                fontSize={{xxs: 14, md: 16}}
                lineHeight="21px"
                fontWeight={500}
                sx={{
                  lineClamp: '3',
                  '-webkit-line-clamp': '2',
                  '-webkit-box-orient': 'vertical',
                  textOverflow: 'ellipsis',
                  overflow: 'hidden',
                  wordWrap: 'break-word',
                  display: 'block',
                  alignItems: 'center',
                }}
              >
                {companyName ? companyName : data?.source?.split(' Jobs')[0]}
              </Typography>
              {!data.showcaseData &&
              data.ambitionBoxData?.aggregateRatingOneDecimal ? (
                <Box data-testid="company-tile-rating" display="flex">
                  <Image
                    src={Star}
                    alt={'star'}
                    width={16}
                    height={16}
                    style={{marginTop: 2, marginLeft: 8}}
                  />
                  <Typography
                    data-testid="company-tile-rating-value"
                    sx={{fontSize: 14, fontWeight: 'bold', opacity: 0.7, ml: 1}}
                  >
                    {data?.ambitionBoxData?.aggregateRatingOneDecimal}
                  </Typography>
                  <Box
                    sx={{
                      width: '3px',
                      backgroundColor: '#7E7E7E',
                      ml: 1,
                      height: '16px',
                      mt: 0.25,
                      opacity: 0.6,
                    }}
                  ></Box>
                  <Typography
                    data-testid="company-tile-reviews-link"
                    component="a"
                    href={addUtmParamsToUrl(
                      data.ambitionBoxData.url,
                      'RATING_REVIEWS',
                      useMediaQuery('(min-width:900px)')
                    )}
                    onClick={() => {
                      trackEvent();
                    }}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{fontSize: 14, fontWeight: 400, color: '#000', ml: 1}}
                  >
                    {data?.ambitionBoxData?.reviewsCount} reviews
                  </Typography>
                </Box>
              ) : (
                <></>
              )}
            </Box>
            <Box
              data-testid="company-tile-subtitle"
              className="joblist__subtitle"
              sx={{
                display: 'flex',
                columnGap: 1,
                rowGap: 0.5,
                alignItems: 'center',
                color: theme.palette.text.secondary,
                flexWrap: 'nowrap',
                mb: '10px',
                mt: '5px',
              }}
            >
              <TooltipHirist
                data-testid="company-tile-location-tooltip"
                arrow
                id="display_location"
                className="set-tooltip"
                title={
                  <>
                    <Box
                      component={'ul'}
                      sx={{listStyleType: 'square', paddingLeft: 2}}
                    >
                      {location?.map((info: any, index: number) => (
                        <Box
                          component={'li'}
                          fontSize={16}
                          fontWeight={'normal'}
                          key={index}
                          padding={'2px 1px'}
                        >
                          {info.name}
                        </Box>
                      ))}
                    </Box>
                  </>
                }
              >
                <Typography
                  data-testid="company-tile-location"
                  variant="subtitle2"
                  fontSize={{xxs: 14, md: 14}}
                  lineHeight="21px"
                  fontWeight={500}
                  sx={{
                    opacity: 0.8,
                    lineClamp: '3',
                    '-webkit-line-clamp': '2',
                    '-webkit-box-orient': 'vertical',
                    textOverflow: 'ellipsis',
                    overflow: 'hidden',
                    wordWrap: 'break-word',
                    display: 'block',
                    alignItems: 'center',
                  }}
                >
                  {location?.length > 1
                    ? 'Multiple Locations'
                    : location?.[0]?.name}
                </Typography>
              </TooltipHirist>
              {!data.showcaseData &&
                location?.length > 0 &&
                data.jobCount > 0 && (
                  <>
                    <Dot2 />{' '}
                    <Typography
                      data-testid="company-tile-jobcount"
                      fontSize={{xxs: 14, md: 14}}
                      lineHeight="21px"
                      fontWeight={500}
                      opacity={0.8}
                    >
                      {data.jobCount} Jobs
                    </Typography>
                  </>
                )}
            </Box>
            <Box
              data-testid="company-tile-description"
              sx={{
                fontSize: '14px',
                fontWeight: 'normal',
                fontStyle: 'normal',
                fontStretch: 'normal',
                lineHeight: 1.5,
                letterSpacing: 'normal',
                color: 'rgba(0,0,0,0.8)',
                mt: '5px',
                mb: '10px',
              }}
              dangerouslySetInnerHTML={createMarkup(introText)}
            />
            {!data.showcaseData && !isLarge && (
              <Link
                data-testid="company-tile-viewall-link"
                href={`/${data.slug}-careers`}
                passHref
                target="_blank"
                rel="noopener noreferrer"
                style={{textDecoration: 'none'}}
                onClick={() => {
                  const {refText} = getRefTagAndOrigin();
                  const {centralisedCompanyId, companyName} = data;

                  // Track company details click event
                  TrackEventUBA('companyDetailsClick', 'click', {
                    ref: refText || '',
                    page: getUbaPageName(),
                    companyID: centralisedCompanyId || '',
                    companyName: companyName || '',
                    companyURL: `/${data.slug}-careers`,
                  });
                }}
              >
                <Button
                  data-testid="company-tile-viewall-btn"
                  sx={{
                    border: `1px solid ${theme.palette.primary.main}`,
                    borderRadius: '16px',
                    padding: '5px 20px',
                    display: 'flex',
                    mt: 1,
                    alignItems: 'center',
                  }}
                >
                  <Typography
                    className="text"
                    sx={{
                      fontFamily: 'Nunito Sans',
                      fontSize: 14,
                      color: theme.palette.primary.main,
                      fontWeight: 500,
                    }}
                  >
                    View All Jobs
                  </Typography>
                </Button>
              </Link>
            )}
            {data.showcaseData?.perks?.length ? (
              <>
                <Typography
                  data-testid="company-tile-perks-title"
                  sx={{fontSize: 14, fontWeight: 600, opacity: 0.8, mt: 1.5}}
                >
                  Perks & Benifits
                </Typography>

                <Box
                  data-testid="company-tile-perks-list"
                  display={'flex'}
                  mt={1}
                >
                  {data.showcaseData.perks.slice(0, BenTOSHow).map((e: any) => {
                    return (
                      <TooltipHirist
                        data-testid="company-tile-perk-tooltip"
                        arrow
                        id="display_location"
                        className="set-tooltip"
                        title={e.title}
                      >
                        <Box
                          sx={{
                            borderRadius: '50%',
                            border: '1px solid #e3e3e3',
                            height: 32,
                            width: 32,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            mr: '6px',
                          }}
                        >
                          <Typography
                            style={{
                              fontStyle: 'normal',
                              fontFamily: 'fontello, sans-serif',
                              fontSize: '16px',
                            }}
                          >
                            {String.fromCharCode(parseInt(e.logo, 16))}
                          </Typography>
                        </Box>
                      </TooltipHirist>
                    );
                  })}
                  {data.showcaseData?.perks?.length > BenTOSHow ? (
                    <Box
                      data-testid="company-tile-perks-more"
                      sx={{
                        borderRadius: '50%',
                        border: '1px solid #b4b9d8',
                        height: 32,
                        width: 32,
                        color: '#fff',
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: '#b4b9d8',
                        justifyContent: 'center',
                        mr: '6px',
                      }}
                    >
                      <Typography fontSize={14}>
                        +{data.showcaseData.perks?.length - BenTOSHow}
                      </Typography>
                    </Box>
                  ) : (
                    <></>
                  )}

                  {/* ))} */}
                  <Link
                    data-testid="company-tile-perks-viewall-link"
                    href={`${data.showcaseData.showcaseLink}?ref=jscompanytab#Jobs`}
                    passHref
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{textDecoration: 'none'}}
                    onClick={e => {
                      const {refText} = getRefTagAndOrigin();
                      const {centralisedCompanyId, companyName} = data;

                      // Track company details click event
                      TrackEventUBA('companyDetailsClick', 'click', {
                        ref: refText || '',
                        page: getUbaPageName(),
                        companyID: centralisedCompanyId || '',
                        companyName: companyName || '',
                        companyURL: `${data.showcaseData.showcaseLink}?ref=jscompanytab#Jobs`,
                      });
                    }}
                  >
                    <Button
                      data-testid="company-tile-perks-viewall-btn"
                      sx={{
                        border: `1px solid ${theme.palette.primary.main}`,
                        borderRadius: '16px',
                        marginLeft: 2,
                        padding: '5px 20px',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Typography
                        className="text"
                        sx={{
                          fontFamily: 'Nunito Sans',
                          fontSize: 14,
                          color: theme.palette.primary.main,
                          fontWeight: 500,
                        }}
                      >
                        {isLarge ? 'View All Jobs' : 'All Jobs'}
                      </Typography>
                    </Button>
                  </Link>
                </Box>
              </>
            ) : (
              <></>
            )}
            {!isLarge && data.showcaseData && (
              <Box
                data-testid="company-tile-visit-company-mobile"
                display="flex"
                sx={{
                  justifyContent: 'center',
                  ml: -4,
                  borderTop: '1px solid',
                  borderColor: '#d9e0eb',
                  mt: 1,
                  mb: 0.5,
                }}
              >
                <Typography
                  data-testid="company-tile-visit-company-mobile-link"
                  component="a"
                  href={`${data.showcaseData.showcaseLink}?ref=jscompanytab`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e: any) => {
                    const {refText} = getRefTagAndOrigin();
                    const {centralisedCompanyId, companyName} = data;

                    // Track company details click event
                    TrackEventUBA('companyDetailsClick', 'click', {
                      ref: refText || '',
                      page: getUbaPageName(),
                      companyID: centralisedCompanyId || '',
                      companyName: companyName || '',
                      companyURL:
                        `${data.showcaseData.showcaseLink}?ref=jscompanytab` ||
                        '',
                    });

                    console.log(
                      'handleCompanyClick',
                      data.showcaseData.showcaseLink
                    );
                  }}
                  sx={{
                    textDecoration: 'underline',
                    color: theme.palette.primary.main,
                    cursor: 'pointer',
                    fontSize: 12,
                    fontWeight: 500,
                    textAlign: 'center',
                    paddingTop: '15px',
                  }}
                >
                  VISIT COMPANY PAGE
                </Typography>
              </Box>
            )}
          </Box>
          <Box
            data-testid="company-tile-right"
            onClick={() => {
              const {refText} = getRefTagAndOrigin();
              const {centralisedCompanyId, companyName} = data;

              // Track company details click event
              TrackEventUBA('companyDetailsClick', 'click', {
                ref: refText || '',
                page: getUbaPageName(),
                companyID: centralisedCompanyId || '',
                companyName: companyName || '',
                companyURL: data.showcaseData
                  ? `${data.showcaseData.showcaseLink}?ref=jscompanytab`
                  : `/${data.slug}-careers`,
              });
              if (typeof window !== 'undefined') {
                openUrl(
                  data.showcaseData
                    ? `${data.showcaseData.showcaseLink}?ref=jscompanytab`
                    : `/${data.slug}-careers`,
                  '_blank'
                );
              }
            }}
            sx={{
              width: {xxs: '0%', md: '25%'},

              cursor: 'pointer',
              display: {xxs: 'none', md: 'flex'},
              flexDirection: 'column',
              alignItems: 'flex-end',
              mt: 1,
              mr: 4,
              paddingTop: '8px',
            }} // Add cursor pointer for better UX
          >
            {data.showcaseData ? (
              <>
                <Image
                  data-testid="company-tile-showcase-image"
                  alt="alt"
                  src={data.showcaseData.images[1]}
                  width={142}
                  height={94}
                  style={{borderRadius: 10, border: '1px solid #fff'}}
                ></Image>
                <Typography
                  data-testid="company-tile-visit-company-link"
                  component="a"
                  href={`${data.showcaseData.showcaseLink}?ref=jscompanytab`}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e: any) => {
                    e.stopPropagation(); // Prevent the Box's onClick from triggering

                    const {refText} = getRefTagAndOrigin();
                    const {centralisedCompanyId, companyName} = data;

                    // Track company details click event
                    TrackEventUBA('companyDetailsClick', 'click', {
                      ref: refText || '',
                      page: getUbaPageName(),
                      companyID: centralisedCompanyId || '',
                      companyName: companyName || '',
                      companyURL: `${data.showcaseData.showcaseLink}?ref=jscompanytab`,
                    });

                    console.log(
                      'handleCompanyClick',
                      data.showcaseData.showcaseLink
                    );
                  }}
                  sx={{
                    textDecoration: 'underline',
                    color: theme.palette.primary.main,
                    cursor: 'pointer',
                    fontSize: 11,
                    mr: 2,
                    paddingTop: '15px',
                  }}
                >
                  VISIT COMPANY PAGE
                </Typography>
              </>
            ) : (
              <>
                <Link
                  href={`/${data.slug}-careers`}
                  passHref
                  target="_blank"
                  style={{textDecoration: 'none'}}
                  rel="noopener noreferrer"
                  onClick={e => {
                    e.stopPropagation(); // Prevent the Box's onClick from triggering

                    const {refText} = getRefTagAndOrigin();
                    const {centralisedCompanyId, companyName} = data;

                    // Track company details click event
                    TrackEventUBA('companyDetailsClick', 'click', {
                      ref: refText || '',
                      page: getUbaPageName(),
                      companyID: centralisedCompanyId || '',
                      companyName: companyName || '',
                      companyURL: `/${data.slug}-careers`,
                    });
                  }}
                >
                  <Button
                    data-testid="company-tile-viewall-desktop-btn"
                    sx={{
                      border: `1px solid ${theme.palette.primary.main}`,
                      borderRadius: '16px',
                      marginLeft: 2,
                      padding: '5px 20px',
                      display: 'flex',
                      alignItems: 'center',
                    }}
                  >
                    <Typography
                      className="text"
                      sx={{
                        fontFamily: 'Nunito Sans',
                        fontSize: 14,
                        color: theme.palette.primary.main,
                        fontWeight: 500,
                      }}
                    >
                      View All Jobs
                    </Typography>
                  </Button>
                </Link>
              </>
            )}
          </Box>
        </Box>
      </JoblistPaper>
    </MainComponent>
  );
};

export default JobListComponent;
